import type { NavigateFunction, NavigateOptions } from 'react-router-dom';
import { ROUTE_PATHS } from '../routes/routePaths';

export type AccessErrorRouteState = {
  /** Optional API-specific detail shown below the standard description */
  detail?: string;
  isHtmlContent?: boolean;
  /** correlationId already logged for the failure that led here (e.g. the 403's
   * X-Request-ID from the axios interceptor) — shown/emailed as-is, never re-logged. */
  correlationId?: string;
};

export const ACCESS_DENIED_REDIRECT_ERROR_MESSAGE = 'Redirecting to access denied page';

export function isAccessDeniedRedirectError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message === ACCESS_DENIED_REDIRECT_ERROR_MESSAGE
  );
}

export function navigateToAccessError(
  navigate: NavigateFunction,
  state?: AccessErrorRouteState,
  options?: Pick<NavigateOptions, 'replace'>
): void {
  void navigate(ROUTE_PATHS.ACCESS_ERROR, { state, ...options });
}

export function isAccessErrorPagePath(pathname: string): boolean {
  return (
    pathname === ROUTE_PATHS.ACCESS_ERROR ||
    pathname === ROUTE_PATHS.PUBLIC_ACCESS_ERROR
  );
}

const GENERIC_ACCESS_DETAIL_MESSAGES = new Set([
  'access denied',
  'you do not have access to this report',
  'you do not have access to this report.',
  'do not have access in product',
  'do not have access in product.',
]);

/** Strip API/legacy copy that duplicates the friendly access-error message */
export function normalizeAccessErrorDetail(detail?: string): string | undefined {
  if (!detail?.trim()) {
    return undefined;
  }

  const trimmed = detail.trim();
  const plainText = trimmed.replaceAll(/<[^>]*>/g, ' ').replaceAll(/\s+/g, ' ').trim();
  const normalized = plainText.toLowerCase();

  if (GENERIC_ACCESS_DETAIL_MESSAGES.has(normalized)) {
    return undefined;
  }

  if (
    normalized.length < 80 &&
    (normalized.includes('access denied') || normalized.includes('do not have access'))
  ) {
    return undefined;
  }

  return trimmed;
}
